var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { submitApproval } from '../services/chatApi';
import { requestAiToken } from '../services/aiTokenApi';
import { setStoredAiToken } from '../services/aiTokenStorage';
export function useConfirmation({ conversationId, pageContext, onApproved, onRejected }) {
    const [pendingApprovalId, setPendingApprovalId] = useState(null);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState(null);
    const approve = useCallback((approvalId, opts) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!conversationId)
            return;
        setPendingApprovalId(approvalId);
        setIsPending(true);
        setError(null);
        try {
            const payload = (opts === null || opts === void 0 ? void 0 : opts.isPlan)
                ? { plan_id: approvalId, decision: 'approve' }
                : { action_id: approvalId, decision: 'approve' };
            try {
                yield submitApproval(conversationId, payload, pageContext);
            }
            catch (firstErr) {
                // One-shot 401 retry: refresh the AI token then resubmit once.
                // A failed approval silently disappears without this — the user
                // has no idea the bulk operation was not authorised (design §3b).
                if (axios.isAxiosError(firstErr) && ((_a = firstErr.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                    try {
                        const refreshResp = yield requestAiToken();
                        if (refreshResp.data) {
                            setStoredAiToken(refreshResp.data.token, refreshResp.data.expires_at);
                        }
                    }
                    catch (_b) {
                        // Proceed with the existing token if the refresh call itself fails.
                    }
                    // Re-throw the retry error (if any) into the outer catch.
                    yield submitApproval(conversationId, payload, pageContext);
                }
                else {
                    throw firstErr;
                }
            }
            onApproved === null || onApproved === void 0 ? void 0 : onApproved();
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
        }
        finally {
            setIsPending(false);
            setPendingApprovalId(null);
        }
    }), [conversationId, pageContext, onApproved]);
    const reject = useCallback((approvalId, opts) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!conversationId)
            return;
        setPendingApprovalId(approvalId);
        setIsPending(true);
        setError(null);
        try {
            const payload = (opts === null || opts === void 0 ? void 0 : opts.isPlan)
                ? { plan_id: approvalId, decision: 'reject' }
                : { action_id: approvalId, decision: 'reject' };
            try {
                yield submitApproval(conversationId, payload, pageContext);
            }
            catch (firstErr) {
                if (axios.isAxiosError(firstErr) && ((_a = firstErr.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                    try {
                        const refreshResp = yield requestAiToken();
                        if (refreshResp.data) {
                            setStoredAiToken(refreshResp.data.token, refreshResp.data.expires_at);
                        }
                    }
                    catch (_b) {
                        // Proceed with the existing token if the refresh call itself fails.
                    }
                    yield submitApproval(conversationId, payload, pageContext);
                }
                else {
                    throw firstErr;
                }
            }
            onRejected === null || onRejected === void 0 ? void 0 : onRejected();
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
        }
        finally {
            setIsPending(false);
            setPendingApprovalId(null);
        }
    }), [conversationId, pageContext, onRejected]);
    return { pendingApprovalId, isPending, approve, reject, error };
}
